import { useMemo, useState, useEffect, useRef } from "react";
import { format as dateFormat } from "date-fns";
import { TextField, InputAdornment, Button, Modal, Box, IconButton, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ContentCopy } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DataTable from "../Table/Datatable";
import { DOMAIN_API_HOST } from "../../../configs/main";
import "./Agencies.scss";
import { useNavigate } from "react-router-dom";

const ADMIN_LOGIN_URL = "https://lk.programmatic.ru/admin/sign-in";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cleanDigits = (value) => value.replace(/\D/g, "");

const formatRussianPhone = (value) => {
  const digits = cleanDigits(value);
  if (!digits) return "";
  let normalized = digits;
  if (normalized[0] === "8") {
    normalized = "7" + normalized.slice(1);
  }
  if (normalized[0] !== "7") {
    normalized = "7" + normalized;
  }
  normalized = normalized.slice(0, 11);

  const rest = normalized.slice(1);
  let result = "+7";

  if (rest.length) {
    result += " (" + rest.slice(0, Math.min(3, rest.length));
    if (rest.length >= 3) {
      result += ")";
    }
  }
  if (rest.length > 3) {
    result += " " + rest.slice(3, Math.min(6, rest.length));
  }
  if (rest.length > 6) {
    result += "-" + rest.slice(6, Math.min(8, rest.length));
  }
  if (rest.length > 8) {
    result += "-" + rest.slice(8, Math.min(10, rest.length));
  }

  return result;
};

const isValidPhone = (value) => {
  const digits = cleanDigits(value);
  return digits.length === 11 && digits.startsWith("7");
};

const validateField = (field, value) => {
  switch (field) {
    case "name":
      return value.trim() ? null : "Обязательное поле";
    case "email": {
      const trimmed = value.trim();
      if (!trimmed) return "Обязательное поле";
      return emailRegex.test(trimmed) ? null : "Некорректная почта";
    }
    case "phone":
      if (!value.trim()) return "Обязательное поле";
      return isValidPhone(value) ? null : "Введите номер в формате +7 (XXX) XXX-XX-XX";
    case "inn": {
      const digits = cleanDigits(value);
      if (!digits) return "Обязательное поле";
      return [10, 12].includes(digits.length) ? null : "ИНН должен содержать 10 или 12 цифр";
    }
    default:
      return null;
  }
};

const columns = [
  { Header: "", accessor: "redirect", width: 120, minWidth: 80, maxWidth: 220 },
  { Header: "ID", accessor: "id", width: 120, minWidth: 90, maxWidth: 200 },
  { Header: "Название", accessor: "name", width: 200, minWidth: 120, maxWidth: 400, isSticky: true, },
  { Header: "Почта", accessor: "email", width: 200, minWidth: 120, maxWidth: 400, isSticky: true, },
  { Header: "Номер телефона", accessor: "phone", width: 190, minWidth: 120, maxWidth: 250 },
  { Header: "ИНН", accessor: "inn", width: 140, minWidth: 120, maxWidth: 200 },
  { Header: "Количество клиентов", accessor: "clientsCount", width: 150, minWidth: 120, maxWidth: 200 },
  { Header: "Дата регистрации", accessor: "registeredAt", width: 160, minWidth: 120, maxWidth: 200 },
  { Header: "Баланс", accessor: "balance", width: 140, minWidth: 120, maxWidth: 200 },
  { Header: "Сумма списаний", accessor: "totalCharges", width: 150, minWidth: 120, maxWidth: 220 },
  { Header: "Дата последнего списания", accessor: "lastChargeDate", width: 190, minWidth: 140, maxWidth: 240 },
  { Header: "Дата последнего пополнения", accessor: "lastTopUpDate", width: 200, minWidth: 140, maxWidth: 260 },
];

const currencyFormat = (value) =>
  typeof value === "number" ? `${value.toLocaleString("ru-RU")} ₽` : "-";

const getToken = () => {
  return localStorage.getItem("admin_token");
};

const fetchAgencies = async (params) => {
  const token = getToken();
  const queryParams = new URLSearchParams();

  if (params.page !== undefined) queryParams.append("page", params.page);
  if (params.limit !== undefined) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);

  const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/agencies?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: "Ошибка при загрузке агентств" }));
    throw new Error(errorData.detail || "Ошибка при загрузке агентств");
  }

  return response.json();
};

function Agencies() {
  const [agencies, setAgencies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [newAgency, setNewAgency] = useState({
    name: "",
    email: "",
    phone: "",
    inn: "",
  });
  const [createdAgencyInfo, setCreatedAgencyInfo] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [depositModal, setDepositModal] = useState(false);
  const [currentAgency, setCurrentAgency] = useState(null);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositError, setDepositError] = useState("");
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const searchDebounceTimer = useRef(null);
  const ITEMS_PER_PAGE = 100;

  useEffect(() => {
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setCurrentPage(1);
    }, 600);

    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchValue]);

  useEffect(() => {
    const loadAgencies = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: ITEMS_PER_PAGE,
        };

        if (debouncedSearchValue.trim()) {
          params.search = debouncedSearchValue.trim();
        }

        const response = await fetchAgencies(params);
        const data = Array.isArray(response.data) ? response.data : [];

        setAgencies(data);
        setTotalPages(Math.max(1, response.total_pages || 1));
      } catch (error) {
        enqueueSnackbar(error.message || "Ошибка при загрузке агентств", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        setAgencies([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAgencies();
  }, [currentPage, debouncedSearchValue, enqueueSnackbar]);

  const resetForm = () => {
    setNewAgency({
      name: "",
      email: "",
      phone: "",
      inn: "",
    });
    setFormErrors({});
  };

  const updateField = (field, value) => {
    setNewAgency((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => {
      const next = { ...prev };
      const fieldError = validateField(field, value);
      if (fieldError) {
        next[field] = fieldError;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const closeModal = () => {
    setOpenModal(false);
    resetForm();
  };

  const validateForm = () => {
    const errors = {};
    (["name", "email", "phone", "inn"]).forEach((field) => {
      const error = validateField(field, newAgency[field]);
      if (error) errors[field] = error;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    let pass = "";
    for (let i = 0; i < 10; i += 1) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  };

  const handleCreateAgency = () => {
    if (!validateForm()) return;

    const nextNumber = agencies.length + 1;
    const id = `AG-${String(nextNumber).padStart(4, "0")}`;
    const today = new Date();
    const generatedPassword = generatePassword();

    const newEntry = {
      id,
      name: newAgency.name.trim(),
      email: newAgency.email.trim(),
      phone: newAgency.phone.trim(),
      inn: newAgency.inn.trim(),
      clientsCount: 0,
      registeredAt: today.toISOString(),
      balance: 0,
      totalCharges: 0,
      lastChargeDate: today.toISOString(),
      lastTopUpDate: today.toISOString(),
    };

    setAgencies((prev) => [newEntry, ...prev]);
    setCreatedAgencyInfo({
      email: newEntry.email,
      password: generatedPassword,
      link: ADMIN_LOGIN_URL,
    });
    setSuccessModalOpen(true);
    enqueueSnackbar("Агентство создано", {
      variant: "success",
      autoHideDuration: 1500,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
    closeModal();
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "-";
    const digits = cleanDigits(phone);
    if (digits.length === 11 && digits.startsWith("7")) {
      return formatRussianPhone(phone);
    }
    return phone;
  };

  const handleCopy = (id) => {
    navigator.clipboard.writeText(String(id));
    enqueueSnackbar("ID агентства скопирован", {
      variant: "success",
      autoHideDuration: 1000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleAccessToken = async (agency_id) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + getToken()
    );
    const response = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/auth/access-token/${agency_id}`, {
      method: "get",
      headers: myHeaders
    });
    const data = await response.json();
    localStorage.setItem("agency_token", data.access_token);
    navigate("/admin/agency-clients");
  };

  const handleOpenDeposit = (agency) => {
    setCurrentAgency(agency);
    setDepositModal(true);
    setDepositAmount("");
    setDepositError("");
  };

  const handleCloseDeposit = () => {
    setDepositModal(false);
    setCurrentAgency(null);
    setDepositAmount("");
    setDepositError("");
  };

  const handleDeposit = async () => {
    if (!depositAmount || !depositAmount.trim()) {
      setDepositError("Введите сумму пополнения");
      return;
    }

    const amount = Number(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      setDepositError("Введите корректную сумму (больше 0)");
      return;
    }

    try {
      const token = getToken();
      const queryParams = new URLSearchParams();
      queryParams.append("agency", currentAgency.id);
      queryParams.append("amount", amount.toString());

      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/agency/deposit?${queryParams.toString()}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: "Ошибка при пополнении баланса" }));
        throw new Error(errorData.detail || "Ошибка при пополнении баланса");
      }

      const updatedAgency = { ...currentAgency, balance: (currentAgency.balance || 0) + amount };
      setAgencies((prev) =>
        prev.map((agency) => (agency.id === currentAgency.id ? updatedAgency : agency))
      );

      enqueueSnackbar("Баланс агентства пополнен", {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });

      handleCloseDeposit();
    } catch (error) {
      setDepositError(error.message || "Ошибка при пополнении баланса");
      enqueueSnackbar(error.message || "Ошибка при пополнении баланса", {
        variant: "error",
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  const tableData = useMemo(() => {
    return agencies.map((agency) => ({
      redirect: (
        <div className="td">
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleAccessToken(agency.id)}
          >
            Перейти
          </Button>
        </div>
      ),
      id: (
        <div className="td" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {String(agency.id).slice(0, 8)}
          <ContentCopy
            fontSize="small"
            className="copyIcon"
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigator.clipboard.writeText(String(agency.id));
              enqueueSnackbar("ID агентства скопирован", {
                variant: "success",
                autoHideDuration: 1000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
              });
            }}
          />
        </div>
      ),
      name: <div className="td">{agency.name || "-"}</div>,
      email: <div className="td">{agency.email || "-"}</div>,
      phone: <div className="td">{formatPhoneNumber(agency.phone)}</div>,
      inn: <div className="td">{agency.inn || "-"}</div>,
      clientsCount: <div className="td">{agency.amount_of_clients || 0}</div>,
      registeredAt: (
        <div className="td">
          {agency.created_at ? dateFormat(new Date(agency.created_at), "dd.MM.yyyy") : "-"}
        </div>
      ),
      balance: (
        <div className="td" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {currencyFormat(agency.balance || 0)}
          <svg
            onClick={() => handleOpenDeposit(agency)}
            style={{ cursor: "pointer" }}
            width="13"
            height="12"
            viewBox="0 0 13 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.49968 1.25391C9.051 1.25391 8.60238 1.424 8.26237 1.76416L2.72868 7.29736C2.62369 7.40236 2.54679 7.53293 2.50603 7.67627L1.76433 10.2734C1.74603 10.3377 1.74524 10.4057 1.76204 10.4704C1.77883 10.535 1.8126 10.594 1.85985 10.6413C1.90709 10.6885 1.96611 10.7223 2.03078 10.7391C2.09545 10.7559 2.16344 10.7551 2.22771 10.7368L4.82536 9.99512L4.82585 9.99463C4.96823 9.9537 5.09848 9.87776 5.20378 9.77246L10.737 4.23877C11.4173 3.55849 11.4173 2.44444 10.737 1.76416C10.397 1.424 9.94835 1.25391 9.49968 1.25391ZM9.49968 1.99951C9.75488 1.99951 10.0097 2.09784 10.2062 2.29443H10.2067C10.6004 2.68815 10.6004 3.31478 10.2067 3.7085L9.72185 4.19336L8.30778 2.7793L8.79265 2.29443C8.98914 2.09784 9.24448 1.99951 9.49968 1.99951ZM7.77751 3.30957L9.19157 4.72363L4.67302 9.24219C4.65836 9.25675 4.6398 9.2679 4.61882 9.27393L2.67058 9.83057L3.22722 7.88184V7.88135C3.23295 7.86119 3.24345 7.84314 3.25896 7.82764L7.77751 3.30957Z"
              fill="black"
            />
          </svg>
        </div>
      ),
      totalCharges: <div className="td">{currencyFormat(agency.amount_of_charges || 0)}</div>,
      lastChargeDate: (
        <div className="td">
          {agency.date_of_last_charge ? dateFormat(new Date(agency.date_of_last_charge), "dd.MM.yyyy") : "-"}
        </div>
      ),
      lastTopUpDate: (
        <div className="td">
          {agency.date_of_last_deposit ? dateFormat(new Date(agency.date_of_last_deposit), "dd.MM.yyyy") : "-"}
        </div>
      ),
    }));
  }, [agencies, enqueueSnackbar]);

  return (
    <div className="AgenciesPage">
      <div className="AgenciesPage-header">
        <div>
          <h2 style={{ textWrap: "nowrap" }}>Агентства</h2>
        </div>
        <div className="AgenciesPage-actions">
          <div className="AgenciesPage-search">
            <TextField
              size="small"
              fullWidth
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Поиск по ID, email, имени, телефону или ИНН"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
      </div>
      <div className="AgenciesPage-table">
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
            <p>Загрузка...</p>
          </div>
        ) : agencies.length === 0 ? (
          <div
            style={{
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(0,0,0,0.6)",
              fontSize: 16,
            }}
          >
            {debouncedSearchValue ? "Агентства не найдены" : "Агентств пока нет"}
          </div>
        ) : (
          <DataTable data={tableData} columns={columns} />
        )}
      </div>
      {!isLoading && agencies.length > 0 && totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
          <button
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "none",
              background: currentPage === 1 ? "#e0e0e0" : "#dfe8ff",
              color: currentPage === 1 ? "#888" : "#3f73f9",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange("prev")}
            disabled={currentPage === 1}
          >
            Назад
          </button>
          <strong style={{ marginTop: 6 }}>
            {currentPage} / {totalPages}
          </strong>
          <button
            style={{
              padding: "6px 16px",
              borderRadius: 6,
              border: "none",
              background: currentPage === totalPages ? "#e0e0e0" : "#dfe8ff",
              color: currentPage === totalPages ? "#888" : "#3f73f9",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
            onClick={() => handlePageChange("next")}
            disabled={currentPage === totalPages}
          >
            Дальше
          </button>
        </div>
      )}
      <Modal open={openModal} onClose={closeModal}>
        <Box className="AgenciesModal">
          <h3>Новое агентство</h3>
          <div className="AgenciesModal-form">
            <TextField
              label="Название"
              value={newAgency.name}
              onChange={(e) => updateField("name", e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
              fullWidth
            />
            <TextField
              label="Почта"
              value={newAgency.email}
              onChange={(e) => updateField("email", e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
              placeholder="agency@example.ru"
            />
            <TextField
              label="Номер телефона"
              value={newAgency.phone}
              onChange={(e) => updateField("phone", formatRussianPhone(e.target.value))}
              error={!!formErrors.phone}
              helperText={formErrors.phone}
              fullWidth
              placeholder="+7 (___) ___-__-__"
            />
            <TextField
              label="ИНН"
              value={newAgency.inn}
              onChange={(e) => updateField("inn", cleanDigits(e.target.value).slice(0, 12))}
              error={!!formErrors.inn}
              helperText={formErrors.inn}
              fullWidth
              placeholder="10 или 12 цифр"
            />
          </div>
          <div className="AgenciesModal-actions">
            <Button variant="outlined" onClick={closeModal}>
              Отмена
            </Button>
            <Button variant="contained" onClick={handleCreateAgency}>
              Создать
            </Button>
          </div>
        </Box>
      </Modal>
      <Modal open={successModalOpen} onClose={() => setSuccessModalOpen(false)}>
        <Box className="AgenciesModal AgenciesModal--success">
          <h3>Агентство создано</h3>
          <p className="AgenciesModal-note">
            Передайте данные агентству, чтобы они вошли в личный кабинет.
          </p>
          {createdAgencyInfo && (
            <div className="AgenciesModal-summary">
              <div className="summary-row">
                <span className="summary-label">Ссылка входа:</span>
                <div className="summary-value">
                  <span style={{ color: "#000", userSelect: "text", wordBreak: "break-all", cursor: "pointer" }}>
                    {createdAgencyInfo.link}
                  </span>
                  <Tooltip title="Скопировать ссылку">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(createdAgencyInfo.link)}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div className="summary-row">
                <span className="summary-label">Почта:</span>
                <div className="summary-value">
                  {createdAgencyInfo.email}
                  <Tooltip title="Скопировать почту">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(createdAgencyInfo.email)}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div className="summary-row">
                <span className="summary-label">Пароль:</span>
                <div className="summary-value">
                  {createdAgencyInfo.password}
                  <Tooltip title="Скопировать пароль">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(createdAgencyInfo.password)}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
          <div className="AgenciesModal-actions">
            <Button variant="contained" onClick={() => setSuccessModalOpen(false)}>
              Понятно
            </Button>
          </div>
        </Box>
      </Modal>
      <Modal open={depositModal} onClose={handleCloseDeposit}>
        <Box className="AgenciesModal">
          <h3>Пополнение баланса агентства</h3>
          {currentAgency && (
            <>
              <p className="AgenciesModal-note" style={{ marginBottom: 20 }}>
                Агентство: {currentAgency.name || currentAgency.id?.slice(0, 8)} ({currentAgency.email})
              </p>
              <p className="AgenciesModal-note" style={{ marginBottom: 20 }}>
                Текущий баланс: {currencyFormat(currentAgency.balance || 0)}
              </p>
            </>
          )}
          <div className="AgenciesModal-form">
            <TextField
              label="Сумма пополнения"
              value={depositAmount}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setDepositAmount(value);
                setDepositError("");
              }}
              error={!!depositError}
              helperText={depositError || "Введите сумму в рублях"}
              fullWidth
              placeholder="Введите сумму"
            />
          </div>
          <div className="AgenciesModal-actions">
            <Button variant="outlined" onClick={handleCloseDeposit}>
              Отмена
            </Button>
            <Button variant="contained" onClick={handleDeposit}>
              Пополнить
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default Agencies;

